import { publicSafeHtmlTheme } from '@/theme/publicSafeTheme';

/**
 * WEB-1988 — раскрутка публичного входа сборки (`public-main.tsx`).
 *
 * В кабинете это делает `App.tsx`: тема, локаль, провайдеры antd. Публичной
 * воронке из всего этого нужны две вещи, и обе — без antd и без стора.
 */

/**
 * Тема полотна воронки — всегда светлая (WEB-490).
 *
 * Кабинет вычисляет тему из профиля/ОС, а плеер обязан её игнорировать: у
 * посетителя профиля нет, и тёмная ОС перекрашивала бы `--color-text-primary`
 * в белый — воронка со светлой палитрой становилась белым по белому. Решение
 * то же и в том же месте правды (`publicSafeHtmlTheme`), что у кабинета — не
 * вторая копия правила, а тот же вызов с «да, это плеер».
 */
export function applyPublicPlayerTheme(): void {
  const theme = publicSafeHtmlTheme('light', true);
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

/**
 * WEB-2015 (П1+П2) — ОБЩИЙ планировщик простоя браузера.
 *
 * ЗАЧЕМ ОБЩИЙ, А НЕ ВТОРАЯ КОПИЯ. До этой правки у `schedulePublicObservability`
 * была своя копия этой логики, а трассировка публичного контура
 * (`sentryPublicTracing`) грузилась ГОЛЫМ `import()` на верхнем уровне
 * `public-main.tsx` — то есть СРАЗУ при исполнении скрипта, а не после
 * первого кадра. На медленной сети (замер 16.09, `state/perf/lab-cwv-2026-09-16.md`)
 * это ловится ДВУМЯ файлами в живом журнале сети: `sdk-*.js` (`@sentry/react`,
 * 81,7 КБ) и `index-*.js` (83,8 КБ — это `@sentry/core`, общий с кабинетным
 * Sentry; Rollup выносит его отдельным чанком без внятного facadeModuleId и
 * называет по умолчанию «index», из-за чего его легко принять за забытый
 * общий вход). Оба файла — части ОДНОГО SDK, и оба уходили за первым экраном
 * вперегонки, а не после него. Эта правка сама по себе не трогала решение о
 * том, ЧТО инициализируется — тем решением занялся WEB-2178 отдельно: см.
 * докблок ниже про то, почему `schedulePublicObservability()` и кабинетная
 * настройка с записью сессии здесь больше не живут вовсе.
 */
export function scheduleOnIdle(run: () => void, timeoutMs = 3000): void {
  const idle = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;

  if (typeof idle === 'function') {
    idle(run, { timeout: timeoutMs });
  } else {
    window.setTimeout(run, 0);
  }
}

/**
 * WEB-2178 — здесь БОЛЬШЕ НЕТ `schedulePublicObservability()`.
 *
 * Функция звала `initSentry()` из `@/config/sentry` — КАБИНЕТНУЮ настройку с
 * включённой записью экрана (session-replay, WEB-469). На публичном входе это
 * была вторая, не задуманная настройка поверх `initPublicTracing()`
 * (`sentryPublicTracing.ts`, звана из `public-main.tsx` ниже по тому же
 * `scheduleOnIdle`), и порядок догрузки чанков решал, какая из двух
 * побеждала на живом экземпляре SDK — живой прогон 16.09 поймал работающую
 * запись экрана на боевой воронке (WEB-2178).
 *
 * УДАЛЕНА, А НЕ ПРОСТО НЕ ВЫЗЫВАЕТСЯ. Пока функция здесь существовала (даже
 * без единого вызывающего), `import('@/config/sentry')` внутри неё оставался
 * ДИНАМИЧЕСКИМ РЕБРОМ графа сборки от `publicBootstrap.ts` — а этот модуль
 * публичный вход тянет всегда (`applyPublicPlayerTheme`, `scheduleOnIdle`).
 * Значит кабинетная настройка оставалась бы ДОСТИЖИМОЙ из публичного графа
 * даже после того, как `public-main.tsx` перестал бы её звать — сторож
 * `publicEntryGuard.ts` (правило `cabinet-sentry`) как раз это и ловит: не
 * вызов, а САМ ПУТЬ в графе. Публичная наблюдаемость (ошибки + трассировка)
 * не потеряна: обе покрыты `initPublicTracing()`, у которого своя, отдельная
 * от кабинета настройка PII (`utils/scrubPii.ts`) без записи экрана.
 */
